import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Seo from '@/components/seo/Seo';
import { content } from '@/content/sr';
import { CONTACT_FORM_PATH } from '@/constants/paths';
import { fetchProjects } from '@/api/projects';
import { breadcrumbJsonLd } from '@/config/seo';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.96, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.45 },
  },
};

const GalleryPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const data = await fetchProjects();
        if (!isMounted) return;
        setProjects(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!isMounted) return;
        setError('Neuspešno učitavanje projekata.');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const pageTitle = 'Projekti — realizovani enterijeri i pergole';
  const pageDescription = `${content.projects.subtitle} Pogledajte realizovane projekte Wall Lux Trade tima u Užicu i širom Srbije.`;

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDescription}
        path="/projekti"
        jsonLd={breadcrumbJsonLd([{ name: 'Projekti', path: '/projekti' }])}
      />

      <div data-testid="projects-page">
        {/* Hero — isti vizuelni jezik kao „O nama“ (slika + overlay) */}
        <section className="relative py-32 md:py-40 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
              alt="Projekti"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-brand-primary/80" />
          </div>
          <div className="container-custom relative z-10 text-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="caption text-brand-accent block mb-4"
            >
              {content.projects.caption}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-5xl md:text-7xl font-light text-white mb-4"
            >
              {content.projects.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-neutral-200 text-lg max-w-2xl mx-auto"
            >
              {content.projects.subtitle}
            </motion.p>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom">
            {loading ? (
              <div className="text-center py-16 text-neutral-500">Učitavanje projekata...</div>
            ) : null}

            {!loading && projects.length > 0 ? (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[240px] md:auto-rows-[260px]"
              >
                {projects.map((project, index) => {
                  const large = index % 5 === 0 || index % 7 === 0;
                  return (
                    <motion.article
                      key={project.relativePath || project.filename || index}
                      variants={itemVariants}
                      className={`group relative overflow-hidden rounded-sm border border-neutral-200 bg-neutral-100 ${
                        large ? 'md:row-span-2' : ''
                      }`}
                      data-testid={`project-gallery-image-${index}`}
                    >
                      <img
                        src={project.imageUrl}
                        alt={project.title || project.filename || 'Projekat'}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-400" />
                      <div className="absolute left-0 right-0 bottom-0 p-4 text-white">
                        <p className="font-heading text-lg leading-tight">
                          {project.title || project.filename}
                        </p>
                        {project.lokacija ? (
                          <p className="text-xs text-white/90 mt-1">{project.lokacija}</p>
                        ) : null}
                      </div>
                    </motion.article>
                  );
                })}
              </motion.div>
            ) : null}

            {!loading && projects.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                className="max-w-3xl mx-auto text-center p-10 md:p-12 bg-neutral-50 border border-neutral-200 rounded-sm shadow-sm"
              >
                <h2 className="font-heading text-2xl md:text-3xl text-brand-primary mb-4">
                  {content.projects.comingSoonTitle}
                </h2>
                <p className="text-neutral-700 leading-relaxed mb-6">
                  {error || content.projects.comingSoonBody}
                </p>
                <p className="text-neutral-600 text-sm md:text-base leading-relaxed mb-8">
                  {content.projects.comingSoonHint}
                </p>
                <Link to={CONTACT_FORM_PATH} className="btn-accent inline-block">
                  {content.projects.cta}
                </Link>
              </motion.div>
            ) : null}
          </div>
        </section>
      </div>
    </>
  );
};

export default GalleryPage;
